import React from 'react';
import _ from 'underscore';

import { defaultMemoize } from 'reselect';
import { Messages } from '../../../codegen/Localize';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { AngularContext } from '../../../ts/factories/AngularContextFactory';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { getMixedContentInOrder } from '../../../contentDefinitionInterfaces/ContentDefinitionsLists';
import { ExplorableItemType } from '../../../ts/models/ContentType';
import { IServerUserInfo } from '../../../ts/models/IServerUserInfo';
import { ISiteUserInfo } from '../../../ts/models/ISiteUserInfo';
import { SiteRoleOptionsResource } from '../../../userManagement/services/resources/SiteRoleOptions';
import { IGetUserSettingsResult } from '../../../ts/ServerApi/requests/GetUserSettingsRequest';
import { getDefaultContentTypeFilterValues } from '../../selectors/ContentFilterSelectors';
import { ISpaceInfoTypeItemCount } from '../../state/ContentCountsState';
import { SiteUserSubspaceName, SpaceInfo, SpaceType, SubspaceName, NullProjectIdForNonProjects } from '../../state/SpaceInfo';
import { ExplorerStyles } from '../Explorer/ExplorerStyles';
import { ExplorerToolbarTestIds } from '../Explorer/ExplorerToolbar';
import { FilterByTypeDropdown } from '../Explorer/FilterByTypeDropdown';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { createContentButton, explorerSubspace } from '../SubspaceHelpers';
import { Subspace, TabbedSpace } from '../TabbedSpace';
import { ConnectedUserSpaceFilterByTypeMenu } from './ConnectedUserSpaceFilterByTypeMenu';
import { siteUserSettingsSubspace } from './SiteUserSettingsSubspace';
import { UserSpaceHeader } from './SiteUserSpaceHeader';
import { ProjectDefinition } from '../../../contentDefinitions/ProjectDefinition';
import { SubscriptionDefinition } from '../../../contentDefinitions/SubscriptionDefinition';
import { DataAlertDefinition } from '../../../contentDefinitions/DataAlertDefinition';
import { CollectionDefinition } from '../../../contentDefinitions/CollectionDefinition';
import { IUserSettings } from '../../../ts/models/IUserSettings';
import { NewCollectionButton } from '../Collections/NewCollectionButton';

export interface SiteUserSpaceStateProps {
  readonly contentTypeFilterValues: ReadonlyArray<ExplorableItemType>;
  readonly defaultContentTypeFilterValues: ReadonlyArray<ExplorableItemType>;
  readonly showWorkbooksAsViews: boolean;
  readonly spaceInfo: SpaceInfo;
  readonly contentCounts: ISpaceInfoTypeItemCount<'siteuser'>;
  readonly contentCountsUpToDate: boolean;
  readonly isDataAlertsEnabled: boolean;
  readonly isFlowsEnabled: boolean;
}

export interface SiteUserSpaceDispatchProps {
  readonly filterSelected: (selectedItems: ArrayLike<ExplorableItemType>) => void;
  readonly fetchUpdatedContentCounts: () => void;
  readonly toggleShowWorkbooksAsViews: (showWorkbooksAsViews: boolean) => void;
  readonly clearContentCounts: (spaceType: SpaceType) => void;
}

export interface SiteUserSpaceOwnProps extends ReactDirectiveProps {
  readonly user: IServerUserInfo | ISiteUserInfo;
  readonly siteRole: string;
  readonly urls: SpacesSiteUrls;
  readonly userSettings: IUserSettings;
  readonly isSessionUserSelf: boolean;
  readonly isSessionServerAdmin: boolean;
  readonly isSessionSiteAdmin: boolean;
  readonly isServerDomainFamilyActiveDirectory: boolean;
  readonly siteRoleOptions: SiteRoleOptionsResource;
}

export type SiteUserSpaceProps = SiteUserSpaceStateProps & SiteUserSpaceOwnProps & SiteUserSpaceDispatchProps;

export class SiteUserSpace extends React.PureComponent<SiteUserSpaceProps> {

  private memoizedGetSiteUserSubspaces = defaultMemoize(getSiteUserSubspaces);

  public componentDidUpdate(): void {
    this.fetchUpdatedCounts();
  }

  public componentDidMount(): void {
    this.fetchUpdatedCounts();
  }

  private static siteUrlBySubspace: Record<SiteUserSubspaceName, (urls: SpacesSiteUrls, username: string, domainName: string) => string> = {
    content: (urls, username, domainName) =>  urls.userContentUrl(username, domainName),
    subscriptions: (urls, username, domainName) =>  urls.userSubscriptionsUrl(username, domainName),
    alerts: (urls, username, domainName) =>  urls.userDataAlertsUrl(username, domainName),
    collections: (urls, username, domainName) => urls.userCollectionsUrl(username, domainName),
    settings: (urls, username, domainName) =>  urls.userSettingsUrl(username, domainName),
  };

  public getSiteUserUrl = (subspaceName?: SiteUserSubspaceName) => {
    let url = this.props.urls.userUrl(this.props.user.username, this.props.user.domainName);
    if (subspaceName) {
      const urlFn = SiteUserSpace.siteUrlBySubspace[subspaceName];
      url = urlFn && urlFn(this.props.urls, this.props.user.username, this.props.user.domainName) || url;
    }

    return url;
  }

  public render() {
    const subspaces = this.memoizedGetSiteUserSubspaces(
      this.props.angularContext,
      this.props.contentCounts,
      this.props.contentCountsUpToDate,
      this.props.isSessionServerAdmin || this.props.isSessionSiteAdmin || this.props.isSessionUserSelf,
      this.props.isSessionUserSelf,
      this.props.isDataAlertsEnabled,
      this.props.userSettings,
      this.props.user as ISiteUserInfo,
      this.props.urls,
      this.props.contentTypeFilterValues,
      this.props.defaultContentTypeFilterValues,
      this.props.showWorkbooksAsViews,
      this.props.filterSelected,
      this.props.toggleShowWorkbooksAsViews,
      this.props.clearContentCounts,
      this.props.spaceInfo.subspaceName,
      this.props.isFlowsEnabled
    );

    const rawSubspaceName = this.props.spaceInfo.subspaceName;
    const subspaceTabName: SubspaceName = rawSubspaceName;

    return (
      <TabbedSpace
        subspaces={subspaces}
        activeSubspaceName={subspaceTabName}
        urlForSubspace={this.getSiteUserUrl}
        mastheadElement={this.mastheadElement()}
        testIdPrefix={'siteuser'}
        spaceItemName={this.props.user.displayName}
      />
    );
  }

  private mastheadElement(): React.ReactNode {
    return (
      <UserSpaceHeader
        item={this.props.user}
        inServerArea={false}
        siteRole={this.props.siteRole}
        siteRoleOptions={this.props.siteRoleOptions}
        userSettings={this.props.userSettings}
        isSessionUserSelf={this.props.isSessionUserSelf}
        isSessionServerAdmin={this.props.isSessionServerAdmin}
        isSessionSiteAdmin={this.props.isSessionSiteAdmin}
        isServerDomainFamilyActiveDirectory={this.props.isServerDomainFamilyActiveDirectory}
      />
    );
  }

  private fetchUpdatedCounts = (): void => {
    this.props.fetchUpdatedContentCounts();
  }
}

function contentFilterControl(
  contentTypeFilterValues: ReadonlyArray<ExplorableItemType>,
  defaultContentTypeFilterValues: ReadonlyArray<ExplorableItemType>,
  showWorkbooksAsViews: boolean,
  filterSelected: (selectedItems: ArrayLike<ExplorableItemType>) => void,
  toggleShowWorkbooksAsViews: (showWorkbooksAsViews: boolean) => void,
  clearContentCounts: (spaceType: SpaceType) => void
): JSX.Element {
  return (
    <React.Fragment>
      <span id={ExplorerToolbarTestIds.FilterByTypeDropdownButtonLabel} className={ExplorerStyles.ToolbarDropdownLabel}>
         {Messages.filterByTypeDropdown_label()}
      </span>
      {!FeatureFlags.contentTypeMenuUpdate &&
        <FilterByTypeDropdown
          filterSelected={filterSelected}
          contentFilter={contentTypeFilterValues}
          defaultContentTypeFilterValues={defaultContentTypeFilterValues}
          menuPanelClass={ConnectedUserSpaceFilterByTypeMenu}
          showWorkbooksAsViews={showWorkbooksAsViews}
          toggleShowWorkbooksAsViews={toggleShowWorkbooksAsViews}
          clearContentCounts={clearContentCounts}
          spaceType={'siteuser'}
          contentItemDefinitions={getMixedContentInOrder()}
        />}
      {FeatureFlags.contentTypeMenuUpdate &&
        <FilterByTypeDropdown
          filterSelected={filterSelected}
          contentFilter={contentTypeFilterValues}
          defaultContentTypeFilterValues={defaultContentTypeFilterValues}
          showWorkbooksAsViews={showWorkbooksAsViews}
          toggleShowWorkbooksAsViews={toggleShowWorkbooksAsViews}
          clearContentCounts={clearContentCounts}
          spaceType={'siteuser'}
          contentItemDefinitions={getMixedContentInOrder()}
        />}
    </React.Fragment>
  );
}

function getSiteUserContentSubspaceCount(currentSubspace: SubspaceName, isFlowsEnabled: boolean, contentCounts: ISpaceInfoTypeItemCount<'siteuser'>, contentTypeFilterValues: ReadonlyArray<ExplorableItemType>) {
  let contentTypes = contentTypeFilterValues;

  if(currentSubspace !== 'content') {
    contentTypes = getDefaultContentTypeFilterValues({item: null, spaceType: 'siteuser', subspaceName: 'content'}, isFlowsEnabled);
  }

  return contentCounts && (
            (_.contains(contentTypes, 'project') ? contentCounts['project'] : 0) +
            (_.contains(contentTypes, 'workbook') ? contentCounts['workbook'] : 0) +
            (_.contains(contentTypes, 'view') ? contentCounts['view'] : 0) +
            (_.contains(contentTypes, 'metric') ? contentCounts['metric'] : 0) +
            (_.contains(contentTypes, 'publishedConnection') ? contentCounts['publishedConnection'] : 0) +
            (_.contains(contentTypes, 'datasource') ? contentCounts['datasource'] : 0) +
            (_.contains(contentTypes, 'flow') ? contentCounts['flow'] : 0) +
            (_.contains(contentTypes, 'dataRole') ? contentCounts['dataRole'] : 0) +
            (_.contains(contentTypes, 'lens') ? contentCounts['lens'] : 0)
  );
}

function getSiteUserSubspaces(
  angularContext: AngularContext,
  contentCounts: ISpaceInfoTypeItemCount<'siteuser'>,
  contentCountsUpToDate: boolean,
  isSelfOrAdmin: boolean,
  isSessionUserSelf: boolean,
  isDataAlertsEnabled: boolean,
  userSettings: IUserSettings,
  siteUser: ISiteUserInfo,
  urls: SpacesSiteUrls,
  contentTypeFilterValues: ReadonlyArray<ExplorableItemType>,
  defaultContentTypeFilterValues: ReadonlyArray<ExplorableItemType>,
  showWorkbooksAsViews: boolean,
  filterSelected: (selectedItems: ArrayLike<ExplorableItemType>) => void,
  toggleShowWorkbooksAsViews: (showWorkbooksAsViews: boolean) => void,
  clearContentCounts: (spaceType: SpaceType) => void,
  subspaceName: SubspaceName,
  isFlowsEnabled: boolean
): ReadonlyArray<Subspace> {
  return _.compact<_.List<Subspace | false>>([
    explorerSubspace({
      name: 'content',
      tabLabel: Messages.content(),
      supportedDisplayModes: ProjectDefinition.supportedDisplayModes,
      supportFilterPanel: true,
      angularContext,
      contentCount: getSiteUserContentSubspaceCount(subspaceName, isFlowsEnabled, contentCounts, contentTypeFilterValues),
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled',
      toolbarContentFilterControl: contentFilterControl(
        contentTypeFilterValues,
        defaultContentTypeFilterValues,
        showWorkbooksAsViews,
        filterSelected,
        toggleShowWorkbooksAsViews,
        clearContentCounts
      ),
      toolbarContentSpecificControls: {
        beginning: createContentButton({ projectId: NullProjectIdForNonProjects, spaceType: 'siteuser' })
      },
    }),
    isSelfOrAdmin && isDataAlertsEnabled && explorerSubspace({
      name: 'alerts',
      tabLabel: Messages.dataAlerts(),
      supportedDisplayModes: DataAlertDefinition.supportedDisplayModes,
      supportFilterPanel: false,
      angularContext,
      contentCount: contentCounts && contentCounts['alerts'],
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled',
      ...(isSessionUserSelf && {emptyStateProps: {
        primaryMessage: Messages.tasksEmptyAlertsSelf()
      }}),
    }),
    isSelfOrAdmin && explorerSubspace({
      name: 'subscriptions',
      tabLabel: Messages.subscriptions(),
      supportedDisplayModes: SubscriptionDefinition.supportedDisplayModes,
      supportFilterPanel: false,
      angularContext,
      contentCount: contentCounts && contentCounts['subscriptions'],
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled',
      ...(isSessionUserSelf && {emptyStateProps: {
        primaryMessage: Messages.tasksEmptySubscriptionsSelf()
      }}),
    }),
    FeatureFlags.collectionsEnabled && explorerSubspace({
      name: 'collections',
      tabLabel: Messages.collections(),
      supportedDisplayModes: CollectionDefinition.supportedDisplayModes,
      supportFilterPanel: true,
      angularContext,
      contentCount: contentCounts && contentCounts['collections'],
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled',
      toolbarContentSpecificControls: {
        beginning: createNewCollectionButton(urls, siteUser.displayName)
      },
    }),
    isSelfOrAdmin && !!userSettings && siteUserSettingsSubspace({
      angularContext,
      isSessionUserSelf,
      userSettings,
      siteUser,
      urls
    })
  ]);
}

function createNewCollectionButton(urls: SpacesSiteUrls, userDisplayName: string): JSX.Element {
  return (<NewCollectionButton
    urls={urls}
    userDisplayName={userDisplayName} />);
}